import { matchPath, generatePath as routerGenPath } from "react-router-dom";

import type {
  IExtractParams,
  IPathParam,
  IPaths,
  IRouteTranslation,
  IBaseBreadcrumb,
  IRoutesBreadcrumbs,
  IMatchedBreadcrumbs,
  IPagesParseKey,
  ICombinationBreadcrumbs,
  IRouteMetadataObj,
  IExtendedRouteObject,
  IBreadcrumb,
  IPartialRouteMetadataObj,
} from "./types";

import { useUIStore } from "@/stores/uiStore";

export const extractSearchFromUrl = (request: Request) => {
  return new URL(request.url)?.searchParams;
};

export const fromHandling = {
  fromParmaName: "from",

  /**
   * return `from` param to set in the url (only for make-appointment page)
   * @param request
   * @returns
   */
  createFromParam(request: Request) {
    const params = new URLSearchParams();

    params.set(this.fromParmaName, new URL(request.url).pathname);
    return params.toString();
  },

  /**
   * read and return 'from' parma from the url
   * @param request
   * @returns
   */
  getFromParmaValue(request: Request) {
    const params = new URL(request.url).searchParams;

    return params.get(this.fromParmaName);
  },
};

export function generatePath<T extends string>(
  path: T,
  params: IExtractParams<T> extends undefined ? null : IExtractParams<T>,
  search?: string | string[][] | Record<string, string> | URLSearchParams,
) {
  return generate(path, params, search);
}

function generate<T>(
  path: T,
  params?: IExtractParams<T> | null,
  search?: string | string[][] | Record<string, string> | URLSearchParams,
) {
  const searchParams = `?${new URLSearchParams(search).toString()}`;
  const _params = params ?? {};

  let url = routerGenPath(`${path}`, _params);

  url += search ? searchParams : "";
  return url;
}

/**
 * Creates path functions for the given routes configuration.
 * Route object that must be readonly otherwise route params will not be extracted properly
 * @template TRoutes The routes configuration type.
 * @template TRoutesParam The route parameters configuration type.
 * @param routes The routes configuration.
 * @returns The paths configuration.
 *
 * @example
 *
 * const routes = {
 *   list: '/list',
 *   view: '/view/:id',
 * } as const;
 *
 * type Params = IExtractRouteParamMap<typeof routes>;
 *
 * const paths = createPaths<typeof routes, Params>(routes);
 *  Result:
 *  {
 *    list: () => string;
 *    view: (params: { id: string }) => string;
 *  }
 */
export function createPaths<TRoutes, TRoutesParam extends IPathParam<TRoutes>>(
  routes: Record<keyof TRoutes, string>,
): IPaths<TRoutes, TRoutesParam> {
  const pathCreators: Partial<IPaths<TRoutes, TRoutesParam>> = {};

  for (const key in routes) {
    const route = routes[key];

    pathCreators[key] = route.includes(":")
      ? (((params: TRoutesParam[typeof key]) =>
          routerGenPath(route, params)) as IPaths<
          TRoutes,
          TRoutesParam
        >[typeof key])
      : ((() => routerGenPath(route)) as IPaths<
          TRoutes,
          TRoutesParam
        >[typeof key]);
  }

  return pathCreators as IPaths<TRoutes, TRoutesParam>;
}

export function createBreadcrumbObject<TRoute>(
  routes: Record<keyof TRoute, string>,
  routesTranslation: IRouteTranslation<TRoute>,
): IRoutesBreadcrumbs<TRoute> {
  const breadcrumbObj: Partial<IRoutesBreadcrumbs<TRoute>> = {};

  for (const key in routes) {
    if (Object.prototype.hasOwnProperty.call(routes, key)) {
      const element = routes[key];
      const breadcrumb: IBaseBreadcrumb = {
        route: element,
        title: routesTranslation[key],
      };

      breadcrumbObj[key] = breadcrumb;
    }
  }

  return breadcrumbObj as IRoutesBreadcrumbs<TRoute>;
}

export function matchedBreadcrumb(
  breadcrumb: IBaseBreadcrumb | ICombinationBreadcrumbs | null,
  pathname: string,
): IMatchedBreadcrumbs[] | null {
  if (breadcrumb === null) return null;

  const path = Array.isArray(breadcrumb.route)
    ? breadcrumb.route.at(-1)
    : breadcrumb.route;

  if (path === undefined) return null;

  const match = matchPath(path, pathname);

  if (match !== null) {
    const params = match.params;

    if (Array.isArray(breadcrumb.route)) {
      return breadcrumb.route.map((item, index) => {
        const matchedBreadcrumbs: IMatchedBreadcrumbs = {
          link: routerGenPath(item, params),
          title: breadcrumb.title[index] as IPagesParseKey,
        };

        return matchedBreadcrumbs;
      });
    } else {
      const matchedBreadcrumbs: IMatchedBreadcrumbs[] = [
        {
          link: routerGenPath(breadcrumb.route, params),
          title: breadcrumb.title as IPagesParseKey,
        },
      ];

      return matchedBreadcrumbs;
    }
  }
  return null;
}

export function createRoute<TRoute>(
  routeObj: Record<keyof TRoute, string>,
  routeObjMetadata: IRouteMetadataObj<TRoute>,
  breadcrumbObj: IRoutesBreadcrumbs<TRoute>,
): IExtendedRouteObject[] {
  const routes: IExtendedRouteObject[] = [];

  for (const key in routeObj) {
    if (Object.prototype.hasOwnProperty.call(routeObj, key)) {
      const element = routeObj[key];
      const routeMetadata = routeObjMetadata[key];
      const breadcrumb = breadcrumbObj[key];
      const route: IExtendedRouteObject = {
        route: {
          path: element,
          lazy: routeMetadata.lazy,
          loader: ({ request }) => {
            useUIStore
              .getState()
              .setPage(
                request,
                routeMetadata.title,
                breadcrumb,
                routeMetadata.mode,
              );
            return {};
          },
        },
        permissions: routeMetadata.permission,
      };

      routes.push(route);
    }
  }
  return routes;
}

export function concatRoutesMetadata<TRoute>(
  baseRouteMetadata: IRouteMetadataObj<TRoute>,
  partialRouteMetadata: IPartialRouteMetadataObj<TRoute>,
) {
  const result = { ...baseRouteMetadata };

  for (const key in result) {
    if (Object.prototype.hasOwnProperty.call(result, key)) {
      const concat = {
        ...baseRouteMetadata[key],
        ...partialRouteMetadata[key],
      } as IRouteMetadataObj<TRoute>[Extract<keyof TRoute, string>];

      result[key] = concat;
    }
  }
  return result;
}

function concat(value1: string | string[], value2: string | string[]) {
  if (typeof value1 === "string") {
    if (typeof value2 === "string") return [value1, value2];

    return [value1, ...value2];
  }
  if (typeof value2 === "string") return [...value1, value2];

  return [...value1, ...value2];
}

export function rebase<TRoute>(
  breadcrumbObj: IRoutesBreadcrumbs<TRoute>,
  name: keyof TRoute,
) {
  const selectedBreadcrumb = breadcrumbObj[name];
  const _breadCrumbs = { ...breadcrumbObj };

  for (const key in _breadCrumbs) {
    if (
      Object.prototype.hasOwnProperty.call(_breadCrumbs, key) &&
      key !== name
    ) {
      const breadcrumb = _breadCrumbs[
        key
      ] as unknown as ICombinationBreadcrumbs;

      breadcrumb.route = concat(selectedBreadcrumb.route, breadcrumb.route);
      breadcrumb.title = concat(
        selectedBreadcrumb.title,
        breadcrumb.title,
      ) as unknown as IPagesParseKey[];
    }
  }
  return _breadCrumbs;
}

export function concatBreadcrumbs<TRoute>(
  baseBreadcrumb: IBreadcrumb,
  childBreadcrumb: IRoutesBreadcrumbs<TRoute>,
) {
  const result: Partial<IRoutesBreadcrumbs<TRoute>> = {};

  for (const key in childBreadcrumb) {
    if (Object.prototype.hasOwnProperty.call(childBreadcrumb, key)) {
      const element = childBreadcrumb[key];

      const concatBreadcrumb: IBreadcrumb = {
        route: concat(baseBreadcrumb.route, element.route),
        title: concat(
          baseBreadcrumb.title,
          element.title,
        ) as unknown as IPagesParseKey[],
      };

      result[key] = concatBreadcrumb;
    }
  }
  return result as IRoutesBreadcrumbs<TRoute>;
}
